import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import LiveShipmentMap from '../components/LiveShipmentMap';
import LowStockTicker from '../components/LowStockTicker';
import DashboardCustomizer, { CustomizerTrigger } from '../components/DashboardCustomizer';
import MyArFollowups from '../components/MyArFollowups';
import StockValuationWidget from '../components/StockValuationWidget';
import InboundCapacityBanner from '../components/InboundCapacityBanner';
import { useAuth } from '../context/AuthContext';
import { RecentSignupsContent } from './admin/RecentSignups';
import { getDashboard, getHealthScore, wsEventsUrl } from '../lib/api';
import { Package, MapPin, Truck, Warning, ArrowUp, ArrowDown, Pulse, Gauge, UserPlus } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

// Widget catalogue — keep IDs in sync with backend
// routers/platform/dashboard_layout.py::WIDGET_CATALOG.
const WIDGET_META = {
  low_stock_ticker:    { title: 'Low-stock alerts',      description: 'Warehouse-wide rows below minimum', multi: true, supports_warehouse: true },
  health_score:        { title: 'Warehouse Health Score',description: 'Overall grade + sub-factors' },
  live_events:         { title: 'Live Activity feed',    description: 'Real-time operational events' },
  main_stats:          { title: 'Headline stats',        description: 'SKUs / Locations / Low stock / Shipped' },
  order_pipeline:      { title: 'Order Pipeline',        description: 'Pending → Picking → Packed → Shipped' },
  inventory_by_status: { title: 'Inventory by Status',   description: 'Available / Reserved / Damaged / Quarantine' },
  shipment_map:        { title: 'Live Shipment Map',     description: 'Outbound shipment positions' },
  recent_movements:    { title: 'Recent Movements',      description: 'Last stock adjustments' },
  stock_valuation:     { title: 'Stock Valuation',       description: 'Total cost + retail + margin of on-hand stock' },
};

const DEFAULT_LAYOUT = [
  { widget_id: 'low_stock_ticker',    visible: true, size: 'full', config: {} },
  { widget_id: 'health_score',        visible: true, size: 'full', config: {} },
  { widget_id: 'live_events',         visible: true, size: 'full', config: {} },
  { widget_id: 'main_stats',          visible: true, size: 'full', config: {} },
  { widget_id: 'order_pipeline',      visible: true, size: 'full', config: {} },
  { widget_id: 'inventory_by_status', visible: true, size: 'full', config: {} },
  { widget_id: 'shipment_map',        visible: true, size: 'full', config: {} },
  { widget_id: 'recent_movements',    visible: true, size: 'full', config: {} },
  { widget_id: 'stock_valuation',     visible: true, size: 'half', config: {} },
];


const StatCard = ({ icon: Icon, label, value, color = 'text-foreground', bgColor = 'bg-card' }) => {
  const isBrand = bgColor === 'bg-brand';
  return (
    <div className={`${bgColor} border border-border p-5 rounded-lg shadow-sm`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-wider mb-2 ${isBrand ? 'text-brand-foreground/70' : 'text-muted-foreground'}`}>{label}</p>
          <p className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</p>
        </div>
        <div className={`w-10 h-10 flex items-center justify-center rounded-md ${isBrand ? 'bg-card/20' : 'bg-muted'}`}>
          <Icon size={22} weight="bold" className={isBrand ? 'text-brand-foreground' : 'text-muted-foreground'} />
        </div>
      </div>
    </div>
  );
};

const OrderStatusCard = ({ label, value, status }) => {
  const statusColors = {
    pending: 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-950/40 dark:border-amber-900 dark:text-amber-300',
    picking: 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-300',
    packed: 'bg-green-50 border-green-200 text-green-700 dark:bg-green-950/40 dark:border-green-900 dark:text-green-300',
    shipped: 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-900 dark:text-indigo-300',
  };
  return (
    <div className={`border p-4 rounded-lg ${statusColors[status] || 'bg-card border-border text-foreground'}`}>
      <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-2xl font-semibold mt-1 tracking-tight">{value}</p>
    </div>
  );
};

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperadmin = user?.platform_role === 'superadmin';
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveEvents, setLiveEvents] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [catalog, setCatalog] = useState(null);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const wsRef = useRef(null);

  // ---- Fetch dashboard + health + layout ----
  useEffect(() => {
    (async () => {
      try {
        const [dashRes, healthRes, layoutRes] = await Promise.all([
          getDashboard(),
          getHealthScore().catch(() => ({ data: null })),
          axios.get(`${API}/api/dashboard/layout`, { withCredentials: true }).catch(() => null),
        ]);
        setData(dashRes.data);
        setHealth(healthRes.data);
        if (layoutRes?.data?.widgets?.length) {
          setLayout(layoutRes.data.widgets);
          setCatalog(layoutRes.data.catalog || null);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally { setLoading(false); }
    })();
  }, []);

  // ---- WS live events (unchanged) ----
  useEffect(() => {
    let ws, reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(wsEventsUrl());
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onmessage = (ev) => {
          try {
            const evt = JSON.parse(ev.data);
            if (evt.type === 'connected' || evt.type === 'pong') return;
            setLiveEvents((prev) => [evt, ...prev].slice(0, 20));
            if (['order_created', 'order_shipped', 'pick_completed', 'pack_completed'].includes(evt.type)) {
              getDashboard().then((r) => setData(r.data)).catch(() => {});
            }
          } catch (e) { /* ignore */ }
        };
        ws.onclose = () => {
          setWsConnected(false);
          reconnectTimer = setTimeout(connect, 5000);
        };
        ws.onerror = () => { /* close handler reconnects */ };
      } catch (e) { /* ignore */ }
    };
    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { ws.onclose = null; ws.close(); }
    };
  }, []);

  // ---- Layout persistence ----
  const saveLayout = useCallback(async () => {
    await axios.put(`${API}/api/dashboard/layout`, { widgets: layout }, { withCredentials: true });
  }, [layout]);

  const resetLayout = useCallback(async () => {
    await axios.delete(`${API}/api/dashboard/layout`, { withCredentials: true });
    setLayout(DEFAULT_LAYOUT);
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 w-48"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-slate-200"></div>)}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ---- Widget renderers (receive the layout row so per-instance config flows in) ----
  const widgets = {
    low_stock_ticker: (w) => {
      const wh = w?.config?.warehouse_id || null;
      // Resolve warehouse code → the ticker prefixes it with ` · ` in its own header
      const whMeta = wh && catalog
        ? (catalog.warehouses || []).find((x) => x.id === wh)
        : null;
      const suffix = whMeta ? (whMeta.code || whMeta.name) : '';
      return <LowStockTicker key={w.instance_id} limit={5} warehouseId={wh} titleSuffix={suffix} />;
    },

    health_score: () => health && (
      <div data-testid="health-score-card" className={`mb-6 border-2 p-4 md:p-5 rounded-lg text-foreground ${
        health.overall_score >= 85 ? 'bg-green-50 border-green-500' :
        health.overall_score >= 70 ? 'bg-blue-50 border-blue-500' :
        health.overall_score >= 55 ? 'bg-amber-50 border-amber-500' : 'bg-red-50 border-red-500'
      }`}>
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="flex items-center gap-4 md:shrink-0">
            <Gauge size={40} weight="fill" className={
              health.overall_score >= 85 ? 'text-green-700' :
              health.overall_score >= 70 ? 'text-blue-700' :
              health.overall_score >= 55 ? 'text-amber-700' : 'text-red-700'
            } />
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Warehouse Health Score</p>
              <p className="text-4xl md:text-5xl font-bold leading-none tracking-tight text-foreground" data-testid="health-overall-score">
                {health.overall_score}
                <span className="text-base md:text-lg text-muted-foreground font-semibold ml-2">/ 100</span>
              </p>
              <p className="text-sm font-semibold uppercase tracking-widest mt-1 text-foreground">
                Grade {health.grade} · {health.status}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 flex-1 min-w-0">
            {health.factors.map((f) => (
              <div key={f.name} className="bg-card/80 border border-border p-2 rounded-md min-w-0" data-testid={`health-factor-${f.name.replace(/\s/g, '-').toLowerCase()}`}>
                <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground truncate" title={f.name}>{f.name}</p>
                <p className="text-xl md:text-2xl font-bold tracking-tight">
                  <span className={f.score >= 80 ? 'text-green-700' : f.score >= 60 ? 'text-amber-700' : 'text-red-700'}>{f.score}</span>
                </p>
                <p className="text-[11px] text-muted-foreground truncate" title={f.value}>{f.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    ),

    live_events: () => (liveEvents.length > 0 || wsConnected) && (
      <div className="mb-6 bg-card border border-border rounded-lg p-3" data-testid="live-events-strip">
        <div className="flex items-center gap-2 mb-2">
          <Pulse size={14} weight="bold" className="text-brand" />
          <span className="overline">Live Activity</span>
        </div>
        {liveEvents.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="live-events-empty">
            Waiting for activity... events appear here as pickers/packers work.
          </p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {liveEvents.slice(0, 8).map((ev, i) => (
              <div key={i} className="flex-shrink-0 px-2 py-1 bg-accent border border-border text-xs font-mono rounded" data-testid={`live-event-${i}`}>
                <span className="font-semibold uppercase text-brand">{ev.type}</span>
                {ev.user_email && <span className="text-muted-foreground ml-1">· {ev.user_email}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    ),

    main_stats: () => (
      <div data-testid="widget-main-stats" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Package} label="Total SKUs" value={data?.inventory?.total_skus || 0} />
        <StatCard icon={MapPin} label="Active Locations" value={data?.inventory?.total_locations || 0} />
        <StatCard icon={Warning} label="Low Stock" value={data?.inventory?.low_stock_count || 0} color="text-destructive" />
        <StatCard icon={Truck} label="Shipped Today" value={data?.orders?.shipped_today || 0} color="text-brand-foreground" bgColor="bg-brand" />
      </div>
    ),

    order_pipeline: () => (
      <div data-testid="widget-order-pipeline" className="mb-6">
        <h2 className="text-lg font-semibold mb-4 tracking-tight">Order Pipeline</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <OrderStatusCard label="Pending" value={data?.orders?.pending || 0} status="pending" />
          <OrderStatusCard label="Picking" value={data?.orders?.picking || 0} status="picking" />
          <OrderStatusCard label="Packed" value={data?.orders?.packed || 0} status="packed" />
          <OrderStatusCard label="Shipped Today" value={data?.orders?.shipped_today || 0} status="shipped" />
        </div>
      </div>
    ),

    inventory_by_status: () => (
      <div data-testid="widget-inventory-by-status" className="bg-card border border-border rounded-lg p-5 mb-6">
        <h2 className="text-lg font-semibold mb-4 tracking-tight text-foreground">Inventory by Status</h2>
        <div className="space-y-3">
          {[
            { key: 'available', label: 'Available', color: 'bg-green-500' },
            { key: 'reserved', label: 'Reserved', color: 'bg-blue-500' },
            { key: 'damaged', label: 'Damaged', color: 'bg-red-500' },
            { key: 'quarantine', label: 'Quarantine', color: 'bg-purple-500' },
          ].map(({ key, label, color }) => {
            const value = data?.inventory?.by_status?.[key] || 0;
            const total = Object.values(data?.inventory?.by_status || {}).reduce((a, b) => a + b, 0) || 1;
            const percent = Math.round((value / total) * 100);
            return (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-foreground">{label}</span>
                  <span className="font-mono text-foreground">{value.toLocaleString()}</span>
                </div>
                <div className="h-2 bg-muted rounded">
                  <div className={`h-full ${color} rounded`} style={{ width: `${percent}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ),

    shipment_map: () => (
      <div data-testid="widget-shipment-map" className="mb-6">
        <LiveShipmentMap />
      </div>
    ),

    recent_movements: () => (
      <div data-testid="widget-recent-movements" className="bg-card border border-border rounded-lg p-5 mb-6">
        <h2 className="text-lg font-semibold mb-4 tracking-tight text-foreground">Recent Movements</h2>
        <div className="space-y-2">
          {(data?.recent_movements || []).slice(0, 6).map((movement, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <div className={`w-8 h-8 flex items-center justify-center rounded-md ${
                movement.movement_type === 'receive' ? 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-300' :
                movement.movement_type === 'pick' ? 'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-300' :
                movement.movement_type === 'adjust' ? 'bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-300' :
                'bg-muted text-muted-foreground'
              }`}>
                {movement.movement_type === 'receive' ? <ArrowDown size={16} weight="bold" /> :
                  movement.movement_type === 'pick' ? <ArrowUp size={16} weight="bold" /> :
                  <Package size={16} weight="bold" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate capitalize text-foreground">{movement.movement_type}</p>
                <p className="text-xs text-muted-foreground">Qty: {movement.quantity}</p>
              </div>
              <span className="text-xs text-muted-foreground font-mono">
                {new Date(movement.timestamp).toLocaleTimeString()}
              </span>
            </div>
          ))}
          {(!data?.recent_movements || data.recent_movements.length === 0) && (
            <p className="text-sm text-muted-foreground text-center py-4">No recent movements</p>
          )}
        </div>
      </div>
    ),

    stock_valuation: (w) => (
      <StockValuationWidget costMethod={w?.config?.cost_method || 'landed'} />
    ),
  };

  return (
    <Layout>
      <div data-testid="dashboard-page" className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">{t('dashboard.title', 'Dashboard')}</h1>
            <p className="text-muted-foreground text-sm">{t('dashboard.subtitle', 'Warehouse overview and key metrics')}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <CustomizerTrigger onClick={() => setCustomizerOpen(true)} />
            <div data-testid="ws-status" className="flex items-center gap-2 px-3 py-1.5 border border-border bg-card rounded-md">
              <span className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500 animate-pulse' : 'bg-muted'}`}></span>
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {wsConnected ? t('dashboard.live', 'LIVE') : t('dashboard.offline', 'OFFLINE')}
              </span>
              {liveEvents.length > 0 && (
                <span className="text-xs font-mono text-muted-foreground">· {liveEvents.length} {t('dashboard.events', 'events')}</span>
              )}
            </div>
          </div>
        </div>

        {/* AR follow-ups widget — always visible at the top */}
        <div className="mb-4">
          <MyArFollowups />
        </div>

        {/* Inbound capacity warning — only renders when a warehouse is
            projected to exceed 100% based on in-transit container ETAs. */}
        <InboundCapacityBanner />

        {/* iter157 — Recent Signups widget (superadmin only). Compact view
            of the newest Stockaris tenants so the platform owner can spot
            onboarding issues fast. Full page at /admin/recent-signups. */}
        {isSuperadmin && (
          <div className="mb-4 bg-card border border-border p-4" data-testid="dashboard-recent-signups">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-widest text-foreground flex items-center gap-2">
                <UserPlus size={16} weight="duotone" className="text-brand" />
                Recent Stockaris Signups
              </h3>
              <a href="/admin/recent-signups" className="text-xs text-brand hover:underline" data-testid="dashboard-recent-signups-link">
                View all →
              </a>
            </div>
            <RecentSignupsContent embedded defaultDays={7} defaultLimit={5} />
          </div>
        )}

        {/* Widgets driven by layout — responsive 2-col grid; 'half' items span 1 col, 'full' items span 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {layout.map((w) => {
            if (!w.visible) return null;
            const render = widgets[w.widget_id];
            if (!render) return null;
            const spanClass = w.size === 'half' ? 'lg:col-span-1' : 'lg:col-span-2';
            return (
              <div
                key={w.instance_id || w.widget_id}
                data-testid={`widget-wrap-${w.widget_id}`}
                data-widget-size={w.size}
                className={`${spanClass} col-span-1`}
              >
                {render(w)}
              </div>
            );
          })}
        </div>

        {customizerOpen && (
          <DashboardCustomizer
            layout={layout}
            catalog={catalog}
            onChange={setLayout}
            onSave={saveLayout}
            onReset={resetLayout}
            onClose={() => setCustomizerOpen(false)}
          />
        )}
      </div>
    </Layout>
  );
}
